import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../Config';
import { 
    CheckCircle, XCircle, AlertTriangle, Clock,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight
} from 'lucide-react';


const LogProcesos = ({ codigo }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [goToPage, setGoToPage] = useState('1');

    useEffect(() => {
        const fetchLogs = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_BASE_URL}/procesos/logs?codigo=${codigo}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setLogs(data);
                }
            } catch (error) {
                console.error('Error fetching logs:', error);
            } finally {
                setLoading(false);
            }
        };

        if (codigo) {
            fetchLogs();
        }
    }, [codigo]);

    // Pagination logic
    const totalItems = logs.length;
    const totalPages = Math.ceil(totalItems / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, totalItems);
    const currentItems = logs.slice(startIndex, endIndex);

    const handlePageChange = (newPage) => {
        const page = Math.max(1, Math.min(newPage, totalPages));
        setCurrentPage(page);
        setGoToPage(page.toString());
    };

    const handleGoToPage = (e) => {
        if (e.key === 'Enter') {
            const page = parseInt(goToPage);
            if (!isNaN(page)) {
                handlePageChange(page);
            }
        }
    };

    // Helper: Calculate duration
    const getDuration = (start, end) => {
        if (!end) return '-';
        const startDate = new Date(start);
        const endDate = new Date(end);
        const diffMs = endDate - startDate;
        if (diffMs < 1000) return `${diffMs}ms`;
        const diffSec = (diffMs / 1000).toFixed(2);
        return `${diffSec}s`;
    };

    // Helper: Format Date & Time
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString();
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Clock className="w-10 h-10 animate-spin mb-3 text-blue-500" />
            <p className="font-bold">Cargando auditoría...</p>
        </div>
    );

    if (logs.length === 0) return (
        <div className="text-center py-20 flex flex-col items-center">
            <div className="bg-yellow-50 p-4 rounded-full mb-4">
                <AlertTriangle className="text-yellow-500" size={40} />
            </div>
            <p className="text-gray-800 font-bold text-lg">Sin historial registrado</p>
            <p className="text-gray-400 text-sm">Este proceso aún no ha sido ejecutado.</p>
        </div>
    );

    return (
        <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm bg-white">
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th className="px-4 py-3 font-bold text-slate-500 text-[10px] uppercase tracking-wider w-24">Fecha</th>
                            <th className="px-4 py-3 font-bold text-slate-500 text-[10px] uppercase tracking-wider w-24">Inicio</th>
                            <th className="px-4 py-3 font-bold text-slate-500 text-[10px] uppercase tracking-wider w-24">Fin</th>
                            <th className="px-4 py-3 font-bold text-slate-500 text-[10px] uppercase tracking-wider w-20">Duración</th>
                            <th className="px-4 py-3 font-bold text-slate-500 text-[10px] uppercase tracking-wider w-28 text-center">Estado</th>
                            <th className="px-4 py-3 font-bold text-slate-500 text-[10px] uppercase tracking-wider">Detalles del Resultado</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                        {currentItems.map((log, index) => {
                            const isError = log.estado === 'ERROR';
                            return (
                                <tr key={log.id} className={`${isError ? 'bg-red-50/30' : 'hover:bg-slate-50'} transition-colors`}>
                                    <td className="px-4 py-3 text-[11px] text-slate-700 font-mono">
                                        {formatDate(log.inicio)}
                                    </td>
                                    <td className="px-4 py-3 text-[11px] text-slate-600 font-mono">
                                        {formatTime(log.inicio)}
                                    </td>
                                    <td className="px-4 py-3 text-[11px] text-slate-600 font-mono">
                                        {log.fin ? formatTime(log.fin) : <span className="text-blue-500 italic">En curso...</span>}
                                    </td>
                                    <td className="px-4 py-3 text-[11px] font-bold text-slate-700">
                                        {getDuration(log.inicio, log.fin)}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border ${log.estado === 'EXITO' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                            log.estado === 'ERROR' ? 'bg-red-50 text-red-700 border-red-100' :
                                                'bg-blue-50 text-blue-700 border-blue-100'
                                            }`}>
                                            {log.estado === 'EXITO' && <CheckCircle size={10} />}
                                            {log.estado === 'ERROR' && <XCircle size={10} />}
                                            {log.estado}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-[11px] text-slate-600 font-mono leading-relaxed max-w-xs truncate" title={log.detalles}>
                                        {log.detalles || <span className="text-slate-300 italic text-[10px]">Sin detalles registrados</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Paginación estilo Premium */}
            <div className="p-4 bg-white border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-[11px] font-medium text-slate-500">Ir a</span>
                        <input 
                            type="text" 
                            className="w-10 h-7 border border-slate-200 rounded text-center text-[11px] font-bold outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                            value={goToPage}
                            onChange={(e) => setGoToPage(e.target.value)}
                            onKeyDown={handleGoToPage}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[11px] font-medium text-slate-500">Ver</span>
                        <select 
                            className="h-7 border border-slate-200 rounded text-[11px] font-bold px-1.5 outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-sm"
                            value={rowsPerPage}
                            onChange={(e) => {
                                setRowsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                                setGoToPage('1');
                            }}
                        >
                            {[5, 10, 20, 50].map(val => (
                                <option key={val} value={val}>{val}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="flex items-center gap-1">
                    <button 
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        className={`p-1.5 rounded-lg hover:bg-slate-100 transition-colors ${currentPage === 1 ? 'text-slate-200' : 'text-slate-500'}`}
                    >
                        <ChevronsLeft className="w-4 h-4" />
                    </button>
                    <button 
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`p-1.5 rounded-lg hover:bg-slate-100 transition-colors ${currentPage === 1 ? 'text-slate-200' : 'text-slate-500'}`}
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>

                    <div className="flex items-center mx-2 gap-1">
                        {[...Array(totalPages)].map((_, i) => {
                            const p = i + 1;
                            if (totalPages > 5) {
                                if (p !== 1 && p !== totalPages && Math.abs(p - currentPage) > 1) {
                                    if (p === 2 || p === totalPages - 1) return <span key={p} className="text-slate-300">...</span>;
                                    return null;
                                }
                            }
                            return (
                                <button
                                    key={p}
                                    onClick={() => handlePageChange(p)}
                                    className={`w-7 h-7 rounded-lg text-[11px] font-bold transition-all ${currentPage === p ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-100'}`}
                                >
                                    {p}
                                </button>
                            );
                        })}
                    </div>

                    <button 
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || totalPages === 0}
                        className={`p-1.5 rounded-lg hover:bg-slate-100 transition-colors ${currentPage === totalPages || totalPages === 0 ? 'text-slate-200' : 'text-slate-500'}`}
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                    <button 
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages || totalPages === 0}
                        className={`p-1.5 rounded-lg hover:bg-slate-100 transition-colors ${currentPage === totalPages || totalPages === 0 ? 'text-slate-200' : 'text-slate-500'}`}
                    >
                        <ChevronsRight className="w-4 h-4" />
                    </button>
                </div>

                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50 px-2 py-1 rounded-md border border-slate-100">
                    [{totalItems > 0 ? startIndex + 1 : 0} A {endIndex} DE {totalItems}]
                </div>
            </div>
        </div>
    );
};

export default LogProcesos;
